import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../state/store';

// Components
import { Button } from '../../components/Button';
import { Table } from '../../components/Table';
import { PageLayout } from '../../components/layout';
import { TableColumn, SortDirection } from '../../components/Table/Table.types';
import { Badge } from '../../components/Badge';
import { SearchInput } from '../../components/Search';
import UserDetailsSideNav from '../../components/User/UserDetailsSideNav';
import UserFormModal from '../../components/User/UserFormModal';
import Pagination from '../../components/Pagination';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';

// Types and Services
import { User, UserFilter, UserRole } from '../../types/user.type';
import { getAllUsers, deleteUser } from '../../services/UserService';

// Utilities
import { showSuccessToast, showErrorToast } from '../../lib/ToastUtility';
import { formatDate } from '../../utils/dateFormatter';
import { debounce } from '../../utils/debounce';
import {  FaPlus } from 'react-icons/fa';

const PAGE_SIZE = 20;

/**
 * Users page component using the standardized Table component
 */
const UsersPageStandardized: React.FC = () => {
  // Redux
  const dispatch = useDispatch();
  const isLoading = useSelector((state: RootState) => state.user.loading);
  const userError = useSelector((state: RootState) => state.user.error);

  // Redux state
  const allUsers = useSelector((state: RootState) => state.user.userList);

  // Local state
  const [filters, setFilters] = useState<UserFilter>({
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPagination, setResetPagination] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  
  // Add this new state for immediate UI updates
  const [inputValue, setInputValue] = useState<string>('');
  // Keep searchTerm for debounced filtering
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  
  // Debounced search function (only for filtering)
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      // Only update search term (which triggers filtering) after debounce
      setSearchTerm(value);
    }, 400),
    []
  );

  // Fetch users only on initial load and explicit refresh actions
  useEffect(() => {
    fetchUsers();
  }, [dispatch]); // Removed filters dependency to prevent API calls on filter changes
  
  // Initialize inputValue with initial searchTerm
  useEffect(() => {
    setInputValue(searchTerm || '');
  }, []);

  // Show error toast when Redux error state changes
  useEffect(() => {
    if (userError) {
      showErrorToast(userError);
    }
  }, [userError]);

  // Handle search term changes with minimum character threshold
  useEffect(() => {
    if (searchTerm !== filters.search) {
      // Only search if term is empty (reset) or has minimum length
      if (searchTerm === '' || (searchTerm && searchTerm.length >= 3)) {
        handleSearch(searchTerm || '');
      }
    }
  }, [searchTerm]);


  // Fetch all users at once without search filters
  const fetchUsers = () => {
    dispatch(getAllUsers({
      page: 1,
      pageSize: 1000,
      sortBy: 'name',
      sortOrder: 'asc',
    }) as any);
  };

  // Apply filters and sorting (client-side)
  const applyFiltersAndSort = useCallback(() => {
    // Start with all users instead of the current page
    let result = [...allUsers];

    // Apply filters
    if (filters.name) {
      result = result.filter(user =>
        user.name.toLowerCase().includes(filters.name!.toLowerCase())
      );
    }

    if (filters.email) {
      result = result.filter(user =>
        user.email.toLowerCase().includes(filters.email!.toLowerCase())
      );
    }

    if (filters.role) {
      result = result.filter(user =>
        user.role === filters.role
      );
    }

    if (filters.active !== undefined) {
      result = result.filter(user =>
        user.active === filters.active
      );
    }

    if (filters.search) {
      result = result.filter(user =>
        user.name.toLowerCase().includes(filters.search!.toLowerCase()) ||
        user.email.toLowerCase().includes(filters.search!.toLowerCase()) ||
        (user.role && user.role.toString().toLowerCase().includes(filters.search!.toLowerCase()))
      );
    }

    // Apply sorting
    if (filters.sortBy) {
      result.sort((a, b) => {
        let aValue: any = a[filters.sortBy as keyof User] || '';
        let bValue: any = b[filters.sortBy as keyof User] || '';

        // Handle specific field types
        if (filters.sortBy === 'lastAccess') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        } else {
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }

        if (filters.sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return result;
  }, [allUsers, filters]); // Changed from [users, filters] to [allUsers, filters]

  // Filtered + sorted users (client-side)
  const filteredUsers = useMemo(() => applyFiltersAndSort(), [applyFiltersAndSort]);

  // Current page slice
  const paginatedUsers = useMemo(
    () => filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredUsers, currentPage]
  );

  // Reset pagination when filters/search change
  useEffect(() => {
    setCurrentPage(1);
    setResetPagination((v) => !v);
  }, [filteredUsers]);

  // Handle immediate input changes for responsiveness
  const handleInputChange = (value: string) => {
    // Update UI immediately
    setInputValue(value);
    
    // Pass to debounced search for filtering
    debouncedSearch(value);
  };

  // Handle search with client-side filtering
  const handleSearch = (searchText: string) => {
    // Client-side filtering only
    setFilters(prev => ({
      ...prev,
      search: searchText,
    }));
    
    // No API call needed - the useEffect with [filters, allUsers] dependency
    // will handle filtering and updating the UI
  };

  // Handle filter application - client-side only
  const handleApplyFilters = (newFilters: UserFilter) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  };

  // Handle user click to show details
  const handleUserClick = (user: User) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  // Handle add user button click
  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  // Handle edit user button click
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  // Handle delete user
  const handleDeleteUser = (userId: number) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (userToDelete === null) return;
    dispatch(deleteUser(userToDelete) as any)
      .then(() => {
        fetchUsers();
        showSuccessToast('User deleted successfully');
        if (showUserDetails && selectedUser?.id === userToDelete) {
          setShowUserDetails(false);
          setSelectedUser(null);
        }
      })
      .catch((error: any) => {
        showErrorToast(error.message || 'Failed to delete user');
      })
      .finally(() => {
        setShowDeleteConfirm(false);
        setUserToDelete(null);
      });
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  // Handle manual refresh
  const handleRefreshData = () => {
    fetchUsers();
    showSuccessToast('User data refreshed');
  };

  // Table columns definition - the standardized approach
  const getColumns = (): TableColumn<User>[] => [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      priority: 'high',
      width: '25%',
      format: (_, user) => (
        <div className="flex items-center">
          {user.profileImageURL && (
            <img
              src={user.profileImageURL}
              alt={user.name}
              className="w-8 h-8 rounded-full mr-2 object-cover"
            />
          )}
          <span>{user.name}</span>
        </div>
      )
    },
    {
      id: 'email',
      label: 'Email',
      sortable: true,
      priority: 'medium',
      width: '25%',
    },
    {
      id: 'role',
      label: 'Role',
      priority: 'medium',
      width: '16.66%',
      format: (value) => (
        <Badge
          label={value as string}
          variant="info"
          size="md"
        />
      )
    },
    {
      id: 'active',
      label: 'Status',
      priority: 'medium',
      width: '16.66%',
      format: (value) => (
        <Badge
          label={value ? 'Active' : 'Inactive'}
          variant={value ? 'success' : 'default'}
          size="md"
        />
      )
    },
    {
      id: 'lastAccess',
      label: 'Last Login',
      sortable: true,
      priority: 'low',
      width: '16.66%',
      format: (value) => value
        ? formatDate(value)
        : 'Never'
    }
  ];

  // Handle sort change from Table component
  const handleSortChange = (column: string, direction: SortDirection) => {
    setFilters({
      ...filters,
      sortBy: column,
      sortOrder: direction
    });
  };

  // Render actions - extracted for PageLayout
  const renderActions = () => (
    <Button
      variant="primary"
      size="sm"
      onClick={handleAddUser}
      startIcon={<FaPlus />}
    >
      Add User
    </Button>
  );

  // Render search functionality - extracted for PageLayout
  const renderSearchBox = () => (
    <div className="w-full">
      <div className="flex space-x-2">
        <div className="flex-grow">
          <SearchInput
            value={inputValue}
            placeholder="Search users... (min 3 characters)"
            onChange={handleInputChange}
            showClearButton={true}
            debounce={0} // Disable component's internal debouncing
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefreshData}
          title="Refresh data"
        >
          Refresh
        </Button>
      </div>
      {(filters.name || filters.email || filters.role || filters.active !== undefined || filters.search) && (
        <Button
          variant="secondary"
          size="sm"
          className="mt-2"
          onClick={() => setFilters({ sortBy: 'name', sortOrder: 'asc' })}
        >
          Clear Filters
        </Button>
      )}
    </div>
  );

  // Render pagination controls - extracted for PageLayout
  const renderPagination = () =>
    filteredUsers.length > PAGE_SIZE ? (
      <Pagination
        totalRecords={filteredUsers.length}
        perPageCount={PAGE_SIZE}
        reset={resetPagination}
        setPerPageDataSet={(start, _end) => {
          const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
          setCurrentPage(nextPage);
        }}
      />
    ) : null;

  // Render component using PageLayout
  return (
    <>
      <PageLayout
        title="Users"
        actions={renderActions()}
        filters={renderSearchBox()}
        footer={renderPagination()}
      >
        <div className="overflow-x-auto">
          <Table
            data={paginatedUsers}
            columns={getColumns()}
            sortBy={filters.sortBy}
            sortDirection={filters.sortOrder}
            onSortChange={handleSortChange}
            onRowClick={handleUserClick}
            hoverable={true}
            loading={isLoading}
            emptyMessage="No users found. Adjust filters or add a new user."
          />
        </div>
      </PageLayout>

      {/* Modals */}
      {showAddUserModal && (
        <UserFormModal
          open={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={() => {
            setShowAddUserModal(false);
            // Refresh all data after adding a new user
            fetchUsers();
          }}
        />
      )}

      {showEditUserModal && selectedUser && (
        <UserFormModal
          open={showEditUserModal}
          onClose={() => setShowEditUserModal(false)}
          user={selectedUser}
          onSuccess={() => {
            setShowEditUserModal(false);
            // Refresh all data after editing a user
            fetchUsers();
          }}
        />
      )}

      <UserDetailsSideNav
        isOpen={showUserDetails}
        onClose={() => setShowUserDetails(false)}
        user={selectedUser}
        onEdit={handleEditUser}
        onDelete={handleDeleteUser}
      />

      <ConfirmModal
        open={showDeleteConfirm}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
      />
    </>
  );
};

export default UsersPageStandardized;